import { useCallback, useMemo } from "react"

import { Badge } from "@productlane/ui"

import type { FieldPathSuggestion } from "../model/field-path-suggestions"
import type { SchemaField } from "../model/tool-schema-fields"
import { fieldPathSuggestions } from "../model/field-path-suggestions"
import { AnchoredSuggestions } from "./anchored-suggestions"
import { HighlightedInput } from "./highlighted-input"
import { PathText, tokenizeFieldPath } from "./policy-token-highlight"
import { useSuggestionCombobox } from "./use-suggestion-combobox"

interface Props {
	name: string
	value: string
	onChange: (next: string) => void
	onSubmit: () => void
	fields: readonly SchemaField[]
	taken: readonly string[]
}

export function FieldPathInput({
	name,
	value,
	onChange,
	onSubmit,
	fields,
	taken,
}: Props) {
	const listId = `paths-${name}`

	const suggestions = useMemo(
		() => fieldPathSuggestions({ fields, taken, query: value }),
		[fields, taken, value],
	)

	const combobox = useSuggestionCombobox<FieldPathSuggestion>({
		items: suggestions,
		listId,
		onPick: useCallback(
			(suggestion: FieldPathSuggestion) => onChange(suggestion.path),
			[onChange],
		),
		onEnter: onSubmit,
		closeOnPick: true,
	})

	return (
		<AnchoredSuggestions
			open={combobox.open}
			onOpenChange={(next) => !next && combobox.setDismissed(true)}
			container={combobox.container}
			anchorRef={combobox.anchorRef}
			listId={listId}
			items={suggestions}
			activeIndex={combobox.activeIndex}
			keyOf={(suggestion) => suggestion.path}
			onHover={combobox.setActiveIndex}
			onPick={combobox.pick}
			keyboardRef={combobox.keyboardRef}
			anchor={
				<HighlightedInput
					ref={combobox.anchorRef}
					name={name}
					id={name}
					value={value}
					tokens={tokenizeFieldPath(value)}
					placeholder="metadata.internalNote"
					hint="Dots for nested fields, [] for every list item, * for any key."
					{...combobox.anchorProps}
					onChange={(event) => {
						combobox.setDismissed(false)
						combobox.setActiveIndex(-1)
						onChange(event.target.value)
					}}
				/>
			}
			renderRow={(suggestion) => (
				<>
					<PathText path={suggestion.path} className="flex-none text-xs" />
					<span className="min-w-0 flex-1" />
					<Badge variant={suggestion.wildcard ? "dark-gray" : "transparent"}>
						{suggestion.type}
					</Badge>
				</>
			)}
		/>
	)
}
